import type { ConfiguredAddOn, ConfiguredService } from '@/features/service-plans';
import type { Product } from '@/features/products/types/products.types';
import type { BasketPricingInput, PricedBasket, PricedItemFigures, PricedLineFigures } from '../types/basketPricing.types';
import { decimalToNumber } from './decimalMoney';

// Matches the backend's PriceBasketRequest::SERVICE_INSTANCE_ID: `<service-uuid>:<13-digit-ms>`.
const SERVICE_INSTANCE_ID = /^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}):[0-9]{13}$/i;

/** The catalogue service id a configured service instance id is derived from. */
export const catalogueServiceId = (instanceId: string): string => {
    const match = SERVICE_INSTANCE_ID.exec(instanceId);
    if (!match) {
        throw new Error(`Malformed service instance id: ${instanceId}`);
    }
    return match[1];
};

export interface QuotePricingSource {
    readonly selectedProduct: Product | null;
    readonly resolvedPlanId?: string | null;
    readonly servicePlanSessionId?: string | null;
    readonly configuredServices: readonly ConfiguredService[];
    readonly configuredAddOns: readonly ConfiguredAddOn[];
}

/** Null without a selected product — nothing to price yet. */
export const basketPricingInputFrom = (quote: QuotePricingSource): BasketPricingInput | null => {
    if (!quote.selectedProduct) {
        return null;
    }
    return {
        productId: quote.selectedProduct.id,
        planId: quote.resolvedPlanId ?? null,
        servicePlanSessionId: quote.servicePlanSessionId ?? null,
        services: quote.configuredServices,
        addOns: quote.configuredAddOns,
    };
};

export interface MergedPricedLines {
    readonly services: ConfiguredService[];
    readonly addOns: ConfiguredAddOn[];
    readonly unpricedNames: string[];
    readonly complete: boolean;
    /** The instance ids the server returned a usable figure for. Anything outside it kept its
     *  catalogue price, so a display surface must show a placeholder rather than that amount. */
    readonly pricedInstanceIds: ReadonlySet<string | number>;
}

/** Overwrites each configured line's figures with the server's for the same instance id.
 *  A line absent from the response makes `complete` false: pending, not priced. */
export const mergePricedLines = (
    services: readonly ConfiguredService[],
    addOns: readonly ConfiguredAddOn[],
    pricing: PricedBasket | undefined,
): MergedPricedLines => {
    if (!pricing) {
        return {
            services: [...services],
            addOns: [...addOns],
            unpricedNames: [],
            complete: false,
            pricedInstanceIds: new Set(),
        };
    }

    const serviceLines = new Map(pricing.services.map((line) => [String(line.instanceId), line]));
    const addOnLines = new Map(pricing.addOns.map((line) => [Number(line.instanceId), line]));

    const unpricedNames: string[] = [];
    const pricedInstanceIds = new Set<string | number>();
    let complete = true;

    const mergedServices = services.map((service) => {
        const line = serviceLines.get(service.instanceId);
        if (!line) {
            complete = false;
            return service;
        }
        if (line.unpriced) {
            unpricedNames.push(service.name);
        } else {
            pricedInstanceIds.add(service.instanceId);
        }
        return {
            ...service,
            price: decimalToNumber(line.net),
            gross_price: decimalToNumber(line.gross),
        };
    });

    const mergedAddOns = addOns.map((addOn) => {
        const line = addOnLines.get(addOn.instanceId);
        if (!line) {
            complete = false;
            return addOn;
        }
        if (line.unpriced) {
            unpricedNames.push(addOn.name);
        } else {
            pricedInstanceIds.add(addOn.instanceId);
        }
        return {
            ...addOn,
            price: decimalToNumber(line.net),
            gross_price: decimalToNumber(line.gross),
        };
    });

    return { services: mergedServices, addOns: mergedAddOns, unpricedNames, complete, pricedInstanceIds };
};

/** The server's priced line for a service instance, or undefined while unpriced or flagged unpriced. */
export const pricedServiceLine = (
    pricing: PricedBasket | undefined,
    instanceId: string,
): PricedLineFigures | undefined => {
    const line = pricing?.services.find((candidate) => String(candidate.instanceId) === instanceId);
    return line && !line.unpriced ? line : undefined;
};

export const pricedItemFigures = (
    line: PricedLineFigures | undefined,
    itemId: string,
): PricedItemFigures | undefined => line?.items?.find((item) => item.itemId === itemId);
